import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Image, notification } from "antd";
import { connect } from "react-redux";
import { verifyTicket, validateTicket } from "../../app/store/actions/usherActions";
import QrReader from "modern-react-qr-reader";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import FlashlightOnIcon from "@mui/icons-material/FlashlightOn";
import FlashlightOffIcon from "@mui/icons-material/FlashlightOff";
import WifiIcon from "@mui/icons-material/Wifi";
import RefreshIcon from "@mui/icons-material/Refresh";
import { centered_flex_box } from "../../app/components/Styles";
import ringer from "../../app/components/beep.mp3";
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformButton,
  PlatformTimeline,
  PlatformProgress,
  PlatformScannerPanel,
  PlatformScannerFrame,
} from "../../app/components/platform";
import {
  platformColors,
  platformFonts,
  platformGhostButtonSx,
} from "../../app/components/tickitPlatformTheme";

const scannerGhostSx = {
  ...platformGhostButtonSx,
  color: "rgba(255,255,255,0.5) !important",
  borderColor: "rgba(255,255,255,0.15) !important",
  "&:hover": {
    color: "rgba(255,255,255,0.85) !important",
    borderColor: "rgba(255,255,255,0.3) !important",
    bgcolor: "rgba(255,255,255,0.06) !important",
  },
};

export const Scanner = ({ ticket, isLoading, verifyTicket, validateTicket, verify }) => {
  const Beep = new Audio(ringer);
  const ticketHolder = ticket?.user || ticket?.userInfo;
  const ticketInfo = [];
  const [camera, setCamera] = useState("environment");
  const scannerWrapRef = useRef(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [scanFeed, setScanFeed] = useState([]);
  const [checkedIn, setCheckedIn] = useState(0);
  const lastTicketIdRef = useRef(null);

  function handleCameraFlip() {
    setCamera((c) => (c === "environment" ? "user" : "environment"));
    setTorchOn(false);
  }

  const getVideoTrack = useCallback(() => {
    const video = scannerWrapRef.current?.querySelector("video");
    const stream = video?.srcObject;
    return stream?.getVideoTracks?.()?.[0] ?? null;
  }, []);

  useEffect(() => {
    if (verify) return;
    const id = setInterval(() => {
      const track = getVideoTrack();
      const caps = track?.getCapabilities?.();
      if (caps && "torch" in caps) {
        setTorchSupported(!!caps.torch);
        clearInterval(id);
      }
    }, 400);
    return () => clearInterval(id);
  }, [verify, getVideoTrack]);

  useEffect(() => {
    if (!ticket?._id || verify) return;
    if (lastTicketIdRef.current === ticket._id) return;
    lastTicketIdRef.current = ticket._id;
    const name = ticket?.userInfo?.name || ticket?.user?.name || "Guest";
    const category = ticket?.category || ticket?.userInfo?.category;
    setScanFeed((prev) => [
      {
        id: `${ticket._id}-${Date.now()}`,
        type: "success",
        title: `${name}${category ? ` — ${category}` : ""} ✓`,
        time: "Just now · Scanner",
      },
      ...prev.slice(0, 9),
    ]);
    setCheckedIn((n) => n + 1);
  }, [ticket, verify]);

  const toggleTorch = async () => {
    const track = getVideoTrack();
    if (!track) {
      notification.warning({ message: "Camera not ready yet" });
      return;
    }
    const next = !torchOn;
    try {
      await track.applyConstraints({ torch: next });
      setTorchOn(next);
    } catch {
      try {
        await track.applyConstraints({ advanced: [{ torch: next }] });
        setTorchOn(next);
      } catch {
        notification.warning({
          message: "Flashlight unavailable",
          description: "This browser or device may not support torch while scanning.",
        });
      }
    }
  };

  function handleScan(data) {
    if (data) {
      Beep.play();
      validateTicket({ ticketId: data });
    }
  }

  function ticketData() {
    ticketInfo.length = 0;
    if (!ticket?.userInfo) return;
    Object.keys(ticket.userInfo).forEach((key) => {
      if (key !== "name") ticketInfo.push({ title: key, value: ticket.userInfo[key] });
    });
  }

  const handleReset = () => {
    validateTicket({ ticketId: null });
    setScanFeed([]);
    setCheckedIn(0);
    lastTicketIdRef.current = null;
  };

  const attendancePct = checkedIn > 0 ? Math.min(100, Math.round((checkedIn / Math.max(checkedIn, 50)) * 100)) : 0;

  if (isLoading && !verify) {
    return (
      <PlatformPage>
        <Box sx={{ ...centered_flex_box, minHeight: 320 }}>
          <CircularProgress sx={{ color: platformColors.brand }} />
        </Box>
      </PlatformPage>
    );
  }

  return (
    <PlatformPage maxWidth={false}>
      <PlatformPageHeader
        title="QR Scanner"
        subtitle="Live check-in · point camera at guest QR codes"
        actions={
          <>
            <PlatformButton variant="ghost" size="small" startIcon={<WifiIcon />} disabled>
              Online
            </PlatformButton>
          </>
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          {verify ? (
            <PlatformCard title="Verify ticket holder">
              <Typography sx={{ fontFamily: platformFonts.display, fontWeight: 700, fontSize: 18, mb: 1 }}>
                {ticketHolder?.name}
              </Typography>
              {ticketData()}
              {ticketInfo
                .filter((elem) => elem.value)
                .map((elem) =>
                  elem.title.toLowerCase().includes("image") ? (
                    <Box key={elem.title} sx={{ ...centered_flex_box, mb: 2 }}>
                      <Image src={elem.value} width={150} height={150} />
                    </Box>
                  ) : (
                    <Typography
                      key={elem.title}
                      sx={{ mb: 1.5, fontSize: "13.5px", color: 'var(--platform-text-2)', fontFamily: platformFonts.body }}
                    >
                      {elem.title.charAt(0).toUpperCase() + elem.title.slice(1)}: {elem.value}
                    </Typography>
                  )
                )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                <PlatformButton
                  variant="primary"
                  sx={{ flex: 1, bgcolor: platformColors.success, "&:hover": { bgcolor: "#059669" } }}
                  onClick={() => verifyTicket({ ticketId: ticket._id })}
                >
                  Information is correct
                </PlatformButton>
                <PlatformButton variant="danger" sx={{ flex: 1 }} onClick={() => validateTicket({ ticketId: null })}>
                  Information is incorrect
                </PlatformButton>
              </Stack>
            </PlatformCard>
          ) : (
            <PlatformScannerPanel
              status={
                <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.35)", fontFamily: platformFonts.body }}>
                  {ticket && !verify ? "Processing scan…" : "Waiting for scan…"}
                </Typography>
              }
              actions={
                <>
                  <PlatformButton
                    variant="ghost"
                    size="small"
                    startIcon={torchOn ? <FlashlightOffIcon /> : <FlashlightOnIcon />}
                    onClick={toggleTorch}
                    disabled={!torchSupported}
                    sx={scannerGhostSx}
                  >
                    Torch
                  </PlatformButton>
                  <PlatformButton
                    variant="ghost"
                    size="small"
                    startIcon={<FlipCameraAndroidIcon />}
                    onClick={handleCameraFlip}
                    sx={scannerGhostSx}
                  >
                    Flip
                  </PlatformButton>
                  <PlatformButton
                    variant="ghost"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleReset}
                    sx={scannerGhostSx}
                  >
                    Reset
                  </PlatformButton>
                </>
              }
            >
              <Box ref={scannerWrapRef} sx={{ mx: "auto", width: 160, height: 160 }}>
                <PlatformScannerFrame scanLine={!verify}>
                  <QrReader
                    onScan={handleScan}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    facingMode={camera}
                  />
                </PlatformScannerFrame>
              </Box>
            </PlatformScannerPanel>
          )}
        </Grid>

        <Grid item xs={12} lg={6}>
          <PlatformCard title="Live attendance feed">
            <PlatformTimeline items={scanFeed} emptyMessage="Waiting for scan…" />
            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${platformColors.border}` }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: "14px", color: 'var(--platform-text-2)', fontFamily: platformFonts.body }}>
                  Checked in
                </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: 'var(--platform-text)', fontFamily: platformFonts.body }}>
                  {checkedIn}
                </Typography>
              </Box>
              <PlatformProgress value={attendancePct || (checkedIn ? 72 : 0)} color={platformColors.success} />
              <Typography
                sx={{
                  textAlign: "center",
                  mt: 1.25,
                  fontSize: "12px",
                  color: 'var(--platform-text-3)',
                  fontFamily: platformFonts.body,
                }}
              >
                {checkedIn
                  ? `${checkedIn} guest${checkedIn === 1 ? "" : "s"} checked in this session`
                  : "Scans will appear in the feed"}
              </Typography>
            </Box>
          </PlatformCard>
        </Grid>
      </Grid>
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state?.usher?.isLoading,
  verify: state?.usher?.verify,
  ticket: state?.usher?.ticket,
});

const mapDispatchToProps = { verifyTicket, validateTicket };

export default connect(mapStateToProps, mapDispatchToProps)(Scanner);
